import { Box, Typography } from "@mui/material";
import { alpha, type Theme } from "@mui/material/styles";
import type { ReactNode } from "react";

import { ApiError } from "../../../lib/api.js";
import type { SxStyle } from "../leads/heatStyles.js";

/**
 * Friendly empty / error state used everywhere inside `/sales/*`. The
 * dashboard's sales proxy collapses every upstream failure into a typed
 * `code` string so the UI can render specific guidance instead of a
 * stack trace.
 *
 * Codes the proxy emits:
 *   SALES_AGENT_NOT_CONFIGURED  — SALES_AGENT_URL is missing in .env
 *   SALES_AGENT_UNREACHABLE     — Fastify couldn't open a socket
 *   SALES_AGENT_UPSTREAM_ERROR  — agent answered with a 4xx/5xx
 *   SALES_AGENT_NO_DATA_YET     — agent up + healthy but hasn't found
 *                                  anything yet (cold start / first hour)
 *   SALES_AGENT_BOOTSTRAPPING   — self-knowledge ingest still running
 */
export function SalesEmptyState({
  title,
  description,
  action,
  variant = "info",
}: {
  title: ReactNode;
  description?: ReactNode;
  action?: ReactNode;
  variant?: "info" | "warn" | "bad";
}): JSX.Element {
  const toneSx: Record<"info" | "warn" | "bad", SxStyle> = {
    info: { borderColor: "divider", bgcolor: "surfaceSoft", color: "text.primary" },
    warn: {
      borderColor: (t: Theme) => alpha(t.palette.warning.main, 0.4),
      bgcolor: (t: Theme) => alpha(t.palette.warning.main, 0.05),
      color: "text.primary",
    },
    bad: {
      borderColor: (t: Theme) => alpha(t.palette.error.main, 0.4),
      bgcolor: (t: Theme) => alpha(t.palette.error.main, 0.05),
      color: "text.primary",
    },
  };
  return (
    <Box
      sx={[{ borderRadius: 1, border: 1, p: 4, textAlign: "center" }, toneSx[variant]]}
    >
      <Typography sx={{ fontSize: "1rem", fontWeight: 600, letterSpacing: "-0.01em" }}>
        {title}
      </Typography>
      {description && (
        <Typography
          sx={{ mx: "auto", mt: 1, maxWidth: "36rem", fontSize: 14, lineHeight: 1.6, color: "text.secondary" }}
        >
          {description}
        </Typography>
      )}
      {action && <Box sx={{ mt: 2, display: "flex", justifyContent: "center" }}>{action}</Box>}
    </Box>
  );
}

/**
 * Map `useQuery.error` to the right empty-state copy. The default is
 * tab-agnostic; pass `surface` to tune the copy ("no leads yet" reads
 * very differently between Today and Leads).
 */
export function SalesErrorState({
  error,
  surface = "data",
}: {
  error: unknown;
  surface?: "status" | "leads" | "strategy" | "brain" | "data";
}): JSX.Element {
  if (error instanceof ApiError) {
    if (error.code === "SALES_AGENT_NOT_CONFIGURED") {
      return (
        <SalesEmptyState
          variant="warn"
          title="Sales agent backend isn't wired up yet"
          description="Set SALES_AGENT_URL in the dashboard server's .env (e.g. http://host.docker.internal:8004 in dev), then restart the server."
        />
      );
    }
    if (error.code === "SALES_AGENT_UNREACHABLE") {
      return (
        <SalesEmptyState
          variant="warn"
          title="Sales agent is unreachable"
          description="The autonomous agent isn't responding. Confirm it's running (docker compose up in xop-agents/apps/sales) and that the dashboard can reach SALES_AGENT_URL on the network."
          action={<UpstreamDetails details={error.details} />}
        />
      );
    }
    if (error.code === "SALES_AGENT_BOOTSTRAPPING") {
      return (
        <SalesEmptyState
          variant="info"
          title="Agent is reading xopnetworks.com to learn what XOP sells"
          description="First-boot self-knowledge ingest. Usually takes a couple of minutes — leads will start appearing once it's done."
        />
      );
    }
    if (error.code === "SALES_AGENT_NO_DATA_YET") {
      return (
        <SalesEmptyState
          variant="info"
          title={emptyTitle(surface)}
          description={emptyBody(surface)}
        />
      );
    }
    if (error.code === "SALES_AGENT_UPSTREAM_ERROR") {
      const details = error.details as
        | { upstream_status?: number; upstream_body?: unknown; url?: string }
        | null;
      const fromDetails = details?.upstream_status;
      const fromError = error.status > 0 ? error.status : undefined;
      const upstream = fromDetails ?? fromError;
      const isMissingEndpoint = upstream === 404;
      const isAuthError = upstream === 401 || upstream === 403;
      const isServerError = typeof upstream === "number" && upstream >= 500;
      return (
        <SalesEmptyState
          variant={isMissingEndpoint ? "info" : "bad"}
          title={
            isMissingEndpoint
              ? "This endpoint isn't on the agent yet"
              : isAuthError
                ? "Agent rejected the dashboard's auth"
                : `Agent error while loading ${surface}`
          }
          description={
            isMissingEndpoint
              ? "The autonomous agent hasn't shipped this v2 endpoint yet. The dashboard UI is ready — wire it up and this page lights up."
              : isAuthError
                ? "The dashboard's JWT didn't pass the agent's verifier. Confirm the agent's JWT_SECRET matches the dashboard's."
                : isServerError
                  ? `The agent returned HTTP ${upstream}. Check its logs (docker compose logs -f api worker beat) for the underlying error.`
                  : typeof upstream === "number"
                    ? `The agent returned HTTP ${upstream}. ${error.message}`
                    : `The agent returned an error (${error.message}). Confirm the dashboard server can reach it, then retry.`
          }
          action={<UpstreamDetails details={error.details} />}
        />
      );
    }
  }
  return (
    <SalesEmptyState
      variant="bad"
      title={`Could not load ${surface}`}
      description={error instanceof Error ? error.message : "Unknown error"}
    />
  );
}

function emptyTitle(surface: "status" | "leads" | "strategy" | "brain" | "data"): string {
  switch (surface) {
    case "status":   return "Agent is healthy — leads are on their way";
    case "leads":    return "No leads yet";
    case "strategy": return "Strategy memo not written yet";
    case "brain":    return "Agent hasn't recorded any actions yet";
    default:         return "Nothing to show yet";
  }
}

function emptyBody(surface: "status" | "leads" | "strategy" | "brain" | "data"): string {
  switch (surface) {
    case "status":
      return "The agent is online but hasn't surfaced leads yet. The first batch usually arrives within an hour after the company-self-knowledge bootstrap finishes.";
    case "leads":
      return "Once the agent runs its first vertical sweep, leads with full evidence chains will land here. You can drop a steering note from the header's “Steer the agent” button if you want it to focus somewhere specific.";
    case "strategy":
      return "The strategist writes a fresh memo at the start of every week. Until then, the agent is operating off its initial company-self-knowledge profile.";
    case "brain":
      return "Once the autonomous loop kicks off its first mission, every plan/act/observe/reflect step will stream here in real time.";
    default:
      return "Check back in a moment.";
  }
}

function UpstreamDetails({ details }: { details: unknown }): JSX.Element | null {
  if (!details || typeof details !== "object") return null;
  const d = details as {
    upstream_status?: number;
    upstream_body?: unknown;
    url?: string;
    reason?: string;
  };
  if (!d.upstream_status && !d.url && !d.reason) return null;
  return (
    <Box
      component="details"
      sx={{
        mt: 1,
        maxWidth: "36rem",
        borderRadius: 1,
        border: 1,
        borderColor: "divider",
        bgcolor: "surfaceSoft",
        p: 1,
        textAlign: "left",
        fontSize: 12,
      }}
    >
      <Box component="summary" sx={{ cursor: "pointer", fontWeight: 500, color: "text.disabled" }}>
        Diagnostic details
      </Box>
      <Box component="dl" sx={{ mt: 1, display: "grid", gap: 0.5 }}>
        {d.url && (
          <Row label="URL"><Box component="code" sx={{ wordBreak: "break-all" }}>{d.url}</Box></Row>
        )}
        {typeof d.upstream_status === "number" && (
          <Row label="HTTP status"><code>{d.upstream_status}</code></Row>
        )}
        {d.reason && (
          <Row label="Reason"><Box component="code" sx={{ wordBreak: "break-all" }}>{d.reason}</Box></Row>
        )}
        {d.upstream_body !== undefined && (
          <Row label="Response">
            <Box
              component="pre"
              sx={{ maxHeight: 160, overflow: "auto", borderRadius: 1, bgcolor: "surfaceSoft", p: 1, fontSize: 10 }}
            >
              {typeof d.upstream_body === "string"
                ? d.upstream_body
                : JSON.stringify(d.upstream_body, null, 2)}
            </Box>
          </Row>
        )}
      </Box>
    </Box>
  );
}

function Row({ label, children }: { label: string; children: ReactNode }): JSX.Element {
  return (
    <Box sx={{ display: "grid", gridTemplateColumns: "110px 1fr", alignItems: "baseline", gap: 1 }}>
      <Box component="dt" sx={{ fontSize: 10, fontWeight: 500, textTransform: "uppercase", letterSpacing: "0.05em", color: "text.disabled" }}>
        {label}
      </Box>
      <Box component="dd" sx={{ minWidth: 0, m: 0 }}>{children}</Box>
    </Box>
  );
}
